import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CompanyCulture = () => {
  const [activeTab, setActiveTab] = useState('environment');

  const cultureAspects = {
    environment: {
      title: 'Office Environment',
      icon: 'Building',
      description: 'Our modern workspace is designed to foster creativity, collaboration, and innovation',
      highlights: [
        'Open collaborative spaces with flexible seating arrangements',
        'Dedicated quiet zones for focused deep work sessions',
        'State-of-the-art AI research lab with latest hardware',
        'Wellness areas including meditation room and fitness space'
      ],
      image: 'office-environment'
    },
    activities: {
      title: 'Team Activities',
      icon: 'Users',
      description: 'We believe strong relationships drive exceptional results for our clients',
      highlights: [
        'Weekly tech talks and knowledge sharing sessions',
        'Monthly team building activities and social events',
        'Quarterly off-site retreats for strategic planning',
        'Annual innovation hackathons with client participation'
      ],
      image: 'team-activities'
    },
    methodology: {
      title: 'Work Methodology',
      icon: 'Settings',
      description: 'Our proven methodologies ensure consistent, high-quality delivery across all projects',
      highlights: [
        'Agile development with 2-week sprint cycles',
        'Continuous integration and deployment practices',
        'Regular client feedback loops and iterative improvement',
        'Cross-functional team collaboration on every project'
      ],
      image: 'work-methodology'
    }
  };

  const teamValues = [
    {
      icon: 'Brain',
      title: 'Continuous Learning',
      description: 'We invest in our team\'s growth through training, certifications, and conference attendance'
    },
    {
      icon: 'Heart',
      title: 'Work-Life Balance',
      description: 'Flexible working arrangements and emphasis on personal well-being and family time'
    },
    {
      icon: 'Zap',
      title: 'Innovation Freedom',
      description: '20% time allocation for personal projects and exploring new technologies'
    },
    {
      icon: 'Award',
      title: 'Recognition Culture',
      description: 'Regular acknowledgment of achievements and peer-to-peer appreciation programs'
    }
  ];

  const currentAspect = cultureAspects[activeTab];

  return (
    <section>
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-foreground mb-4">Company Culture</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Our culture is built on collaboration, innovation, and the shared mission of transforming businesses through AI
        </p>
      </div>

      {/* Culture Tabs */}
      <div className="bg-white rounded-2xl shadow-soft border border-border overflow-hidden mb-12">
        {/* Tab Navigation */}
        <div className="border-b border-border">
          <div className="flex">
            {Object.entries(cultureAspects)?.map(([key, aspect]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 px-6 py-4 text-center font-medium transition-smooth ${
                  activeTab === key
                    ? 'bg-primary text-white' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Icon name={aspect?.icon} size={18} />
                  <span className="hidden sm:inline">{aspect?.title}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center">
                  <Icon name={currentAspect?.icon} size={20} color="white" />
                </div>
                <h3 className="text-2xl font-bold text-foreground">{currentAspect?.title}</h3>
              </div>
              
              <p className="text-muted-foreground mb-6 leading-relaxed">
                {currentAspect?.description}
              </p>

              <div className="space-y-4">
                {currentAspect?.highlights?.map((highlight, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></div>
                    <span className="text-muted-foreground">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Visual Mockup */}
            <div className="relative">
              <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-8">
                {activeTab === 'environment' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow-soft">
                      <div className="flex items-center space-x-3">
                        <Icon name="MapPin" size={16} className="text-primary" />
                        <span className="font-medium">Modern Office Space</span>
                      </div>
                      <div className="text-sm text-success">Active</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-white rounded-lg text-center">
                        <Icon name="Users" size={24} className="text-primary mx-auto mb-2" />
                        <div className="text-sm font-medium">Collaboration Zones</div>
                      </div>
                      <div className="p-4 bg-white rounded-lg text-center">
                        <Icon name="Brain" size={24} className="text-primary mx-auto mb-2" />
                        <div className="text-sm font-medium">AI Research Lab</div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'activities' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-white rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium">Upcoming Events</span>
                        <Icon name="Calendar" size={16} className="text-primary" />
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Tech Talk: GPT-4 Applications</span>
                          <span className="text-muted-foreground">This Week</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Team Building: Escape Room</span>
                          <span className="text-muted-foreground">Next Week</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Innovation Hackathon</span>
                          <span className="text-muted-foreground">Next Month</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'methodology' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-white rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium">Current Sprint</span>
                        <div className="text-sm text-success">Sprint 12</div>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2 mb-3">
                        <div className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full" style={{ width: '75%' }}></div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs text-center">
                        <div>
                          <div className="font-medium text-primary">24</div>
                          <div className="text-muted-foreground">Completed</div>
                        </div>
                        <div>
                          <div className="font-medium text-warning">8</div>
                          <div className="text-muted-foreground">In Progress</div>
                        </div>
                        <div>
                          <div className="font-medium text-muted-foreground">4</div>
                          <div className="text-muted-foreground">Pending</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Team Values */}
      <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6 mb-12">
        {teamValues?.map((value, index) => (
          <div key={index} className="text-center group">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center group-hover:scale-110 transition-smooth">
              <Icon name={value?.icon} size={24} className="text-primary" />
            </div>
            <h4 className="font-semibold text-foreground mb-2">{value?.title}</h4>
            <p className="text-sm text-muted-foreground">{value?.description}</p>
          </div>
        ))}
      </div>

      {/* Join Our Team CTA */}
      <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-2xl p-8 lg:p-12 text-center">
        <div className="max-w-2xl mx-auto">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full gradient-primary flex items-center justify-center">
            <Icon name="Briefcase" size={32} color="white" />
          </div>
          <h3 className="text-2xl font-bold text-foreground mb-4">Join Our Team</h3>
          <p className="text-muted-foreground mb-6">
            We're always looking for talented individuals who share our passion for AI innovation 
            and client success. Explore opportunities to grow your career with us.
          </p>
          <div className="flex items-center justify-center space-x-4">
            <Button 
              variant="default"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={() => window.location.href = '/contact-consultation-request'}
            >
              View Open Positions
            </Button>
            <Button 
              variant="outline"
              iconName="Mail"
              iconPosition="left"
              onClick={() => window.location.href = '/contact-consultation-request'}
            >
              Contact HR
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyCulture;